import { httpClient } from '@/shared/api/http-client'
import type { AiModel } from '@/shared/types/ai-model'

export interface UpdateUserSettingsPayload {
  preferredLanguage?: string | null
  preferredAiModel?: AiModel | null
  memoryPreferences?: string | null
}

export const TOKEN_BALANCE_QUERY_KEY = ['token-balance'] as const
export const PREMIUM_PRICING_QUERY_KEY = ['premium-pricing'] as const

export type PremiumPurpose = 'Premium1' | 'Premium2'
export type PaymentProvider = 'CryptoPay'

export const FALLBACK_PREMIUM_PRICING: PremiumPricingResponse = {
  premium1: {
    cryptoPay: { '1': 7, '6': 42, '12': 84 },
  },
  premium2: {
    cryptoPay: { '1': 14, '6': 84, '12': 168 },
  },
}

export interface PremiumPricingResponse {
  premium1: {
    cryptoPay: Record<string, number>
  }
  premium2: {
    cryptoPay: Record<string, number>
  }
}

export interface CreatePremiumInvoicePayload {
  provider: PaymentProvider
  amount: number
  purpose: PremiumPurpose
}

export interface CreatePremiumInvoiceResponse {
  invoiceLink: string
}

interface PremiumPricingNode {
  cryptoPay?: unknown
  CryptoPay?: unknown
}

interface PremiumPricingApiResponse {
  premium1?: PremiumPricingNode
  Premium1?: PremiumPricingNode
  premium2?: PremiumPricingNode
  Premium2?: PremiumPricingNode
}

export async function updateUserSettings(payload: UpdateUserSettingsPayload): Promise<void> {
  await httpClient.post('/api/User/settings', {
    preferredLanguage: payload.preferredLanguage ?? null,
    preferredAiModel: payload.preferredAiModel ?? null,
    memoryPreferences: payload.memoryPreferences ?? null,
  })
}

export async function fetchTokenBalance(): Promise<number> {
  const response = await httpClient.get<{ balance: number }>('/api/user-tokens/balance')
  const balance = typeof response.data.balance === 'number' ? response.data.balance : 0
  return balance
}

export async function fetchAiModels(): Promise<AiModel[]> {
  const response = await httpClient.get<AiModel[]>('/api/Model/ai-models')
  return response.data
}

export async function fetchPremiumPricing(): Promise<PremiumPricingResponse> {
  try {
    const response = await httpClient.get<PremiumPricingApiResponse>('/api/Payment/premium/pricing')
    return normalizePricing(response.data)
  } catch {
    // Graceful fallback so UI keeps working even with legacy backend responses
    return FALLBACK_PREMIUM_PRICING
  }
}

function normalizePricing(raw: PremiumPricingApiResponse | undefined): PremiumPricingResponse {
  const premium1 = raw?.premium1 ?? raw?.Premium1
  const premium2 = raw?.premium2 ?? raw?.Premium2
  const normalized: PremiumPricingResponse = {
    premium1: {
      cryptoPay: normalizeProviderPricing(premium1?.cryptoPay ?? premium1?.CryptoPay),
    },
    premium2: {
      cryptoPay: normalizeProviderPricing(premium2?.cryptoPay ?? premium2?.CryptoPay),
    },
  }

  const hasData =
    Object.keys(normalized.premium1.cryptoPay).length > 0 || Object.keys(normalized.premium2.cryptoPay).length > 0

  return hasData ? normalized : FALLBACK_PREMIUM_PRICING
}

function normalizeProviderPricing(value: unknown): Record<string, number> {
  if (!isRecord(value)) {
    return {}
  }

  const normalized: Record<string, number> = {}
  for (const [duration, price] of Object.entries(value)) {
    if (typeof price === 'number') {
      normalized[duration] = price
    }
  }

  return normalized
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export async function createPremiumInvoice(payload: CreatePremiumInvoicePayload): Promise<CreatePremiumInvoiceResponse> {
  const response = await httpClient.post<CreatePremiumInvoiceResponse>('/api/Payment/premium/invoice', {
    Provider: payload.provider,
    Amount: payload.amount,
    Purpose: payload.purpose,
  })
  return response.data
}
